import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Mail,
  Phone,
  MapPin,
  FileText,
  Calendar,
  Star,
  Send,
  MessageSquare,
  Clock,
  ChevronRight,
  Briefcase,
  ExternalLink,
} from 'lucide-react'
import { candidatesApi, applicationsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { formatDate, getInitials } from '@/lib/utils'
import { toast } from '@/components/ui/toaster'

interface CandidateDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  candidateId: string
  applicationId?: string
}

export function CandidateDrawer({
  open,
  onOpenChange,
  candidateId,
  applicationId,
}: CandidateDrawerProps) {
  const [activeTab, setActiveTab] = useState('overview')
  const [note, setNote] = useState('')
  const queryClient = useQueryClient()

  const { data: candidate, isLoading } = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: () => candidatesApi.get(candidateId),
    enabled: open,
  })

  const addNote = useMutation({
    mutationFn: (content: string) =>
      applicationsApi.addNote(applicationId!, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidate', candidateId] })
      setNote('')
      toast({ title: 'Note added', variant: 'success' })
    },
  })

  const rawCandidate = candidate as any
  const candidateData = rawCandidate ? {
    ...rawCandidate,
    name: rawCandidate.name || `${rawCandidate.firstName || ''} ${rawCandidate.lastName || ''}`.trim() || 'Unknown',
  } : null

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
            onClick={() => onOpenChange(false)}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-xl bg-background border-l shadow-xl z-50 overflow-hidden"
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : candidateData ? (
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-14 w-14">
                      <AvatarImage src={candidateData.avatarUrl} />
                      <AvatarFallback className="text-lg">
                        {getInitials(candidateData.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h2 className="text-xl font-semibold">{candidateData.name}</h2>
                      {candidateData.currentTitle && (
                        <p className="text-muted-foreground">
                          {candidateData.currentTitle}
                          {candidateData.currentCompany && ` at ${candidateData.currentCompany}`}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2">
                        {candidateData.email && (
                          <a
                            href={`mailto:${candidateData.email}`}
                            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                          >
                            <Mail className="h-3.5 w-3.5" />
                            {candidateData.email}
                          </a>
                        )}
                        {candidateData.phone && (
                          <a
                            href={`tel:${candidateData.phone}`}
                            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                          >
                            <Phone className="h-3.5 w-3.5" />
                            {candidateData.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onOpenChange(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                {/* Actions Bar */}
                <div className="flex items-center gap-2 px-6 py-3 border-b bg-muted/30">
                  <Button size="sm">
                    <Send className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                  <Button size="sm" variant="outline">
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule Interview
                  </Button>
                  {candidateData.resumeUrl && (
                    <Button size="sm" variant="outline" asChild>
                      <a href={candidateData.resumeUrl} target="_blank" rel="noopener noreferrer">
                        <FileText className="h-4 w-4 mr-2" />
                        View Resume
                      </a>
                    </Button>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="w-full justify-start px-6 pt-4">
                      <TabsTrigger value="overview">Overview</TabsTrigger>
                      <TabsTrigger value="activity">Activity</TabsTrigger>
                      <TabsTrigger value="evaluations">Evaluations</TabsTrigger>
                      <TabsTrigger value="interviews">Interviews</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="p-6 space-y-6">
                      {/* Quick Info */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Quick Info</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-2">
                          {candidateData.location && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{candidateData.location}</span>
                            </div>
                          )}
                          {candidateData.source && (
                            <div className="flex items-center gap-2">
                              <ExternalLink className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">Source: {candidateData.source}</span>
                            </div>
                          )}
                          {candidateData.experience && (
                            <div className="flex items-center gap-2">
                              <Briefcase className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{candidateData.experience} years exp.</span>
                            </div>
                          )}
                          {candidateData.appliedAt && (
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                Applied {formatDate(candidateData.appliedAt)}
                              </span>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Rating */}
                      {candidateData.rating !== undefined && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Overall Rating</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-center gap-2">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-5 w-5 ${
                                    i < candidateData.rating
                                      ? 'fill-warning text-warning'
                                      : 'text-muted-foreground/30'
                                  }`}
                                />
                              ))}
                              <span className="ml-2 text-sm text-muted-foreground">
                                {candidateData.rating}/5 ({candidateData.evaluationCount || 0} evaluations)
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Applications */}
                      {candidateData.applications && candidateData.applications.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Applications</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            {candidateData.applications.map((app: any) => (
                              <div
                                key={app.id}
                                className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                              >
                                <div>
                                  <p className="font-medium text-sm">{app.job?.title}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {app.stage?.name} • {formatDate(app.appliedAt)}
                                  </p>
                                </div>
                                <Badge
                                  style={{
                                    backgroundColor: app.stage?.color + '20',
                                    color: app.stage?.color,
                                  }}
                                >
                                  {app.stage?.name}
                                </Badge>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )}

                      {/* Add Note */}
                      {applicationId && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Add Note</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <Textarea
                              placeholder="Write a note about this candidate..."
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                              rows={3}
                            />
                            <div className="flex justify-end">
                              <Button
                                size="sm"
                                onClick={() => addNote.mutate(note)}
                                disabled={!note.trim() || addNote.isPending}
                              >
                                <MessageSquare className="h-4 w-4 mr-2" />
                                Add Note
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    <TabsContent value="activity" className="p-6">
                      <div className="space-y-4">
                        {candidateData.activities?.length > 0 ? (
                          candidateData.activities.map((activity: any, index: number) => (
                            <div key={activity.id} className="flex gap-3">
                              <div className="flex flex-col items-center">
                                <div className="w-2 h-2 rounded-full bg-primary" />
                                {index < candidateData.activities.length - 1 && (
                                  <div className="w-px flex-1 bg-border mt-2" />
                                )}
                              </div>
                              <div className="flex-1 pb-4">
                                <p className="text-sm">{activity.description}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {formatDate(activity.createdAt)}
                                  {activity.user && ` by ${activity.user.name}`}
                                </p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-center text-muted-foreground py-8">
                            No activity yet
                          </p>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="evaluations" className="p-6">
                      <div className="space-y-4">
                        {candidateData.evaluations?.length > 0 ? (
                          candidateData.evaluations.map((evaluation: any) => (
                            <Card key={evaluation.id}>
                              <CardContent className="pt-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-8 w-8">
                                      <AvatarFallback className="text-xs">
                                        {getInitials(evaluation.user?.name || 'U')}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="text-sm font-medium">
                                        {evaluation.user?.name}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {formatDate(evaluation.createdAt)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center">
                                    {[...Array(5)].map((_, i) => (
                                      <Star
                                        key={i}
                                        className={`h-4 w-4 ${
                                          i < evaluation.rating
                                            ? 'fill-warning text-warning'
                                            : 'text-muted-foreground/30'
                                        }`}
                                      />
                                    ))}
                                  </div>
                                </div>
                                {evaluation.notes && (
                                  <p className="mt-3 text-sm">{evaluation.notes}</p>
                                )}
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <p className="text-center text-muted-foreground py-8">
                            No evaluations yet
                          </p>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="interviews" className="p-6">
                      <div className="space-y-4">
                        {candidateData.interviews?.length > 0 ? (
                          candidateData.interviews.map((interview: any) => (
                            <Card key={interview.id}>
                              <CardContent className="pt-4">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <p className="font-medium">{interview.title}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {formatDate(interview.scheduledAt)}
                                    </p>
                                  </div>
                                  <Badge
                                    variant={
                                      interview.status === 'completed'
                                        ? 'default'
                                        : interview.status === 'cancelled'
                                        ? 'destructive'
                                        : 'secondary'
                                    }
                                  >
                                    {interview.status}
                                  </Badge>
                                </div>
                                {interview.interviewers && (
                                  <div className="flex items-center gap-2 mt-3">
                                    <span className="text-xs text-muted-foreground">
                                      Interviewers:
                                    </span>
                                    <div className="flex -space-x-2">
                                      {interview.interviewers.map((interviewer: any) => (
                                        <Avatar
                                          key={interviewer.id}
                                          className="h-6 w-6 border-2 border-background"
                                        >
                                          <AvatarFallback className="text-[10px]">
                                            {getInitials(interviewer.name)}
                                          </AvatarFallback>
                                        </Avatar>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <p className="text-center text-muted-foreground py-8">
                            No interviews scheduled
                          </p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Candidate not found
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
